import { useState } from 'react'
import { Button, Menu, MenuItem } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { QueryStatusEnum } from './types'
import { SIZING } from '@common/theme/sizing'
import { ArrowForwardRounded, CloseRounded, RefreshRounded } from '@mui/icons-material'
import { SPACING } from '@common/theme/spacing'
import ReopenQueryDialog, { ContainerSubmission, QueryInfo, TimepointSubmissionInfo } from './ReopenQueryDialog'
import ReasonDialog from '@common/components/ReasonDialog/ReasonDialog'
import { ReopenQueryFormData } from '../schemas/queryValidationSchema'
import { QueryStatus_Input } from '@common/config/api/client'

export interface QueryActionMenuProps {
  query: QueryInfo
  timepoint: TimepointSubmissionInfo
  containerSubmissions: ContainerSubmission[]
  onUpdateStatus: (
    queryId: string,
    status: QueryStatus_Input,
    reason?: string,
    containerIds?: string[],
    requiresTimepointResubmission?: boolean,
  ) => void
  onGoToTimepoint: () => void
}

export default function QueryActionMenu({
  query,
  timepoint,
  containerSubmissions,
  onUpdateStatus,
  onGoToTimepoint,
}: Readonly<QueryActionMenuProps>) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [reasonDialogOpen, setReasonDialogOpen] = useState(false)
  const [reopenDialogOpen, setReopenDialogOpen] = useState(false)

  const open = Boolean(anchorEl)

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleGoToTimepoint = () => {
    if (onGoToTimepoint) {
      onGoToTimepoint()
    }
    handleMenuClose()
  }

  const handleOpenMarkAsUnresolvedDialog = () => {
    setReasonDialogOpen(true)
    handleMenuClose()
  }

  const handleConfirmMarkAsUnresolved = (reason: string) => {
    onUpdateStatus(query.id, QueryStatusEnum.UNRESOLVED, reason)
    setReasonDialogOpen(false)
  }

  const handleCancelMarkAsUnresolved = () => {
    setReasonDialogOpen(false)
  }

  const handleOpenReopenDialog = () => {
    setReopenDialogOpen(true)
    handleMenuClose()
  }

  const handleConfirmReopen = (formData: ReopenQueryFormData) => {
    onUpdateStatus(
      query.id,
      QueryStatusEnum.REOPENED,
      formData.reason,
      formData.requires_timepoint_resubmission ? formData.container_ids : [],
      formData.requires_timepoint_resubmission,
    )
    setReopenDialogOpen(false)
  }

  const handleCancelReopen = () => {
    setReopenDialogOpen(false)
  }

  return (
    <>
      <Button color="secondary" variant="contained" onClick={handleMenuClick} endIcon={<ExpandMoreIcon />}>
        More
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleGoToTimepoint}>
          <ArrowForwardRounded
            sx={{ width: SIZING.popupMenuIconWidth, color: 'text.secondary', mr: SPACING.buttonSpacing }}
          />
          Go to timepoint
        </MenuItem>

        {query.status !== QueryStatusEnum.UNRESOLVED && (
          <MenuItem onClick={handleOpenMarkAsUnresolvedDialog}>
            <CloseRounded
              sx={{ width: SIZING.popupMenuIconWidth, color: 'text.secondary', mr: SPACING.buttonSpacing }}
            />
            Mark as Unresolvable
          </MenuItem>
        )}

        {query.status == QueryStatusEnum.RESOLVED ? (
          <MenuItem onClick={handleOpenReopenDialog}>
            <RefreshRounded
              sx={{ width: SIZING.popupMenuIconWidth, color: 'text.secondary', mr: SPACING.buttonSpacing }}
            />
            Re-open query
          </MenuItem>
        ) : null}
      </Menu>

      <ReasonDialog
        open={reasonDialogOpen}
        title="Mark query as Unresolvable?"
        description="The query and chat will be closed. All assignees and CCs will be notified. You can also provide a reason for them."
        confirmLabel="Mark unresolvable"
        onConfirm={handleConfirmMarkAsUnresolved}
        onCancel={handleCancelMarkAsUnresolved}
      />

      <ReopenQueryDialog
        open={reopenDialogOpen}
        onClose={handleCancelReopen}
        onConfirm={handleConfirmReopen}
        query={query}
        timepoint={timepoint}
        containerSubmissions={containerSubmissions}
      />
    </>
  )
}
